import React from 'react'
import Breadcrumbs from 'components/Common/Breadcrumb';
import { Button, Card, CardBody, Col, Container, FormGroup, Label, Row } from 'reactstrap';
import { MetaTags } from 'react-meta-tags';
import { AppConfiguration } from 'config';
import { useDispatch, useSelector } from 'react-redux';
import { getRuleStats, getRuleNames, getProductsNames, getRuleLogs, getAccountsList } from 'store/admin/dev-dashboard/actions';
import { useState } from 'react';
import ReactMultiselectCheckboxes from 'react-multiselect-checkboxes/lib/ReactMultiselectCheckboxes';
import { useEffect } from 'react';
import CustomDateRangePicker, { createOption, TIME_RANGE_OPTIONS } from 'components/Common/DateRangePicker';
import { SfCommaStringFormatter, getAppropriateLoadingGif, getIANATimezoneStringForAbbreviation, getPrimaryButtonClass, percentage } from 'helpers/sf_utils';
import { RULES_STATUS_OPTIONS, REPORTING_TIMEZONE, RULE_STATUS } from 'helpers/sf_constants';
import Select from "react-select";
import RulesDashboardTable from './RulesDashboardTable';
import StatsCard from '../DevDashboard/StatsCard';

const DEFAULT_PAGE_SIZE = 30
const DEFAULT_SORT = {
  sort_column: 'timestamp',
  sort_direction: 'desc',
}
const DEFAULT_OPTIONS = [
  { label: 'All', value: 0 },
]

const RulesDashboard = () => {
  const {
    current_user,
    rule_names,
    rule_names_loading,
    rule_stats,
    rule_stats_loading,
    product_names,
    product_names_loading,
    accounts_list,
  } = useSelector((state) => ({
    current_user: state.LoginAdminUser.current_user,
    rule_names: state.DevDashboard.rule_names,
    rule_stats: state.DevDashboard.rule_stats,
    rule_names_loading: state.DevDashboard.rule_names_loading,
    rule_stats_loading: state.DevDashboard.rule_stats_loading,
    product_names: state.DevDashboard.product_names,
    product_names_loading: state.DevDashboard.products_names_loading,
    accounts_list: state.DevDashboard.accounts_list,
  }));

  const dispatch = useDispatch();
  //states
  const [selectedRuleName, setSelectedRuleName] = useState([])
  const [selectedProductName, setSelectedProductName] = useState([])
  const [selectedDateRange, setSelectedDateRange] = useState(createOption(
    TIME_RANGE_OPTIONS.LAST_HOUR,
    new Date(),
    getIANATimezoneStringForAbbreviation(current_user?.timezone || REPORTING_TIMEZONE),
  ))
  const [selectedStatusType, setSelectedStatusType] = useState(RULES_STATUS_OPTIONS[0]);
  const [selectedProductType, setSelectedProductType] = useState(RULES_STATUS_OPTIONS[0]);
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [ruleLogsSort, setRuleLogsSort] = useState(DEFAULT_SORT)
  const [appliedRuleLogsFilters, setAppliedRuleLogsFilters] = useState(null)

  useEffect(() => {
    setSelectedRuleName(getRuleName(rule_names))
  }, [rule_names])

  useEffect(() => {
    setSelectedProductName(getProductNames(product_names))
  }, [product_names])

  useEffect(() => {
    setSelectedAccounts(getAccountList(accounts_list))
  }, [accounts_list])

  useEffect(() => {
    dispatch(getRuleNames());
    dispatch(getProductsNames());
    dispatch(getAccountsList());
    applyFilters([], [])
    applyFiltersOnRulesLogsData([], [])
  }, [])

  const getRuleName = (rule_names) => {
    return rule_names?.reduce((a, c) => {
      return [
        ...a,
        {
          label: c?.rule_name,
          value: c?.rule_name
        }
      ]
    }, DEFAULT_OPTIONS)

  }

  const getAccountList = (accounts_list) => {
    return accounts_list?.reduce((a, c) => {
      return [
        ...a,
        {
          label: c?.company_name,
          value: c?.id
        }
      ]
    }, DEFAULT_OPTIONS)

  }

  const getProductNames = (product_names) => {
    return product_names?.reduce((a, c) => {
      return [
        ...a,
        {
          label: c?.product_name,
          value: c?.product_name
        }
      ]
    }, DEFAULT_OPTIONS)
  }

  const getDropdownLabels = ({ placeholderButtonLabel, value }) => {
    if (value && value?.some((o) => o.value === 0)) {
      return <span className='css-1v99tuv text-black align-self-center'>All</span>;
    }
    return <span className='css-1v99tuv text-black align-self-center'>{value?.length} selected</span>;
  }

  const handleSelectLogStream = (selectedOption, event) => {
    const options = getRuleName(rule_names)
    if (event.action === 'select-option' && event.option.value === 0) {
      setSelectedRuleName(options);
    } else if (
      event.action === 'deselect-option'
      && event.option.value === 0
    ) {
      setSelectedRuleName([]);
    } else if (event.action === 'deselect-option') {
      setSelectedRuleName(selectedOption.filter((o) => o.value !== 0));
    } else if (selectedOption.length === options.length - 1) {
      setSelectedRuleName(options);
    } else {
      setSelectedRuleName(selectedOption);
    }
  }

  const handleSelectAccount = (selectedOption, event) => {
    const options = getAccountList(accounts_list);
    if (event.action === 'select-option' && event.option.value === DEFAULT_OPTIONS[0].value) {
      setSelectedAccounts(options);
    } else if (event.action === 'deselect-option' && event.option.value === DEFAULT_OPTIONS[0].value) {
      setSelectedAccounts([]);
    } else if (event.action === 'deselect-option') {
      setSelectedAccounts(selectedOption.filter((o) => o.value !== DEFAULT_OPTIONS[0].value));
    } else if (selectedOption.length === options.length - 1) {
      setSelectedAccounts(options);
    } else {
      setSelectedAccounts(selectedOption);
    }
  };

  const handleSelectProductName = (selectedOption, event) => {
    const options = getProductNames(product_names)
    if (event.action === 'select-option' && event.option.value === 0) {
      setSelectedProductName(options);
    } else if (
      event.action === 'deselect-option'
      && event.option.value === 0
    ) {
      setSelectedProductName([]);
    } else if (event.action === 'deselect-option') {
      setSelectedProductName(selectedOption.filter((o) => o.value !== 0));
    } else if (selectedOption.length === options.length - 1) {
      setSelectedProductName(options);
    } else {
      setSelectedProductName(selectedOption);
    }
  }

  const preparePayload = (selectedRuleName, selectedProductType, selectedAccounts) => {
    const selectedLogRuleValues = selectedRuleName?.map((tag) => tag.value)
    const selectedProductValues = selectedProductType?.map((tag) => tag.value)
    const selectedAccountsValues = selectedAccounts?.map((tag) => tag.value)

    const data = {
      start_date: selectedDateRange?.value?.startDate,
      end_date: selectedDateRange?.value?.endDate,
      status: selectedStatusType?.value,
      rule_name: selectedLogRuleValues.includes(0) ? [] : selectedLogRuleValues,
      product_name: selectedProductValues.includes(0) ? [] : selectedProductValues,
      account_id: selectedAccountsValues?.includes(0) ? [] : selectedAccountsValues,
    }
    if (selectedStatusType?.value === DEFAULT_OPTIONS[0].label) {
      delete data.status
    }
    return data
  }

  const applyFilters = (selectedRuleName, selectedProductType, selectedAccounts) => {
    const data = preparePayload(selectedRuleName, selectedProductType, selectedAccounts)
    dispatch(getRuleStats(data));
  }

  const buildRuleLogsPayload = (filters, pageNumber = 1, sortOptions = ruleLogsSort) => {
    return {
      ...filters,
      page_number: pageNumber,
      page_size: DEFAULT_PAGE_SIZE,
      sort_column: sortOptions.sort_column,
      sort_direction: sortOptions.sort_direction,
    }
  }

  const applyFiltersOnRulesLogsData = (selectedRuleName, selectedProductType, selectedAccounts) => {
    const filters = preparePayload(selectedRuleName, selectedProductType, selectedAccounts)
    setAppliedRuleLogsFilters(filters)
    dispatch(getRuleLogs(buildRuleLogsPayload(filters)))
  }

  const fetchRuleLogsPage = (pageNumber) => {
    const filters = appliedRuleLogsFilters || preparePayload(selectedRuleName, selectedProductName, selectedAccounts)
    dispatch(getRuleLogs(buildRuleLogsPayload(
      filters,
      Math.max(1, Number(pageNumber) || 1),
    )))
  }

  const onRuleLogsSortChange = (sort_column, sort_direction) => {
    const nextSort = { sort_column, sort_direction }
    setRuleLogsSort(nextSort)
    const filters = appliedRuleLogsFilters || preparePayload(selectedRuleName, selectedProductName, selectedAccounts)
    dispatch(getRuleLogs(buildRuleLogsPayload(
      filters,
      1,
      nextSort,
    )))
  }

  return (
    <div className="page-content" id="page-content-wrapper">
      <MetaTags>
        <title>Validation Logs | {AppConfiguration.WEBSITE}</title>
      </MetaTags>
      {rule_names_loading ? getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)
        : <Container fluid>
          <Breadcrumbs title="" breadcrumbItem="Rules Validator" />
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <Row className='d-flex flex-row'>
                    <Col className="align-self-end col-2">
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Accounts</Label>
                        <div>
                          <ReactMultiselectCheckboxes
                            hideSearch
                            getDropdownButtonLabel={getDropdownLabels}
                            isMulti
                            value={selectedAccounts}
                            onChange={(selectedOption, event) => {
                              handleSelectAccount(selectedOption, event);
                            }}
                            options={getAccountList(accounts_list)}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col className="align-self-end col-2">
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Rules</Label>
                        <div>
                          <ReactMultiselectCheckboxes
                            hideSearch
                            getDropdownButtonLabel={getDropdownLabels}
                            isMulti
                            value={selectedRuleName}
                            onChange={(selectedOption, event) => {
                              handleSelectLogStream(selectedOption, event);
                            }}
                            options={getRuleName(rule_names)}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md='2'>
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Time</Label>
                        <div>
                          <CustomDateRangePicker
                            ianaTz={getIANATimezoneStringForAbbreviation(current_user?.timezone || REPORTING_TIMEZONE)}
                            value={selectedDateRange}
                            onChange={setSelectedDateRange}
                            options={Object.keys(TIME_RANGE_OPTIONS).map((opt) => createOption(
                              TIME_RANGE_OPTIONS[opt],
                              new Date(),
                              getIANATimezoneStringForAbbreviation(current_user?.timezone || REPORTING_TIMEZONE),
                            ))}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md='2'>
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Product Types</Label>
                        <div>
                          <ReactMultiselectCheckboxes
                            hideSearch
                            getDropdownButtonLabel={getDropdownLabels}
                            isMulti
                            value={selectedProductName}
                            onChange={(selectedOption, event) => {
                              handleSelectProductName(selectedOption, event);
                            }}
                            options={getProductNames(product_names)}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md='2'>
                      <FormGroup className='mb-0'>
                        <Label className='font-weight-semibold'>Status</Label>
                        <Select
                          name="elapsed_time_type"
                          options={RULES_STATUS_OPTIONS}
                          onChange={setSelectedStatusType}
                          value={selectedStatusType}
                        />
                      </FormGroup>
                    </Col>
                    <Col className="align-self-end">
                      <div>
                        <FormGroup className="mb-0">
                          <Button
                            className={`btn ${getPrimaryButtonClass()}`}
                            onClick={() => {
                              applyFilters(selectedRuleName, selectedProductName, selectedAccounts, getRuleStats)
                              applyFiltersOnRulesLogsData(selectedRuleName, selectedProductName, selectedAccounts)
                            }}>
                            {rule_stats_loading ? 'Loading Data...' : 'Update'}
                          </Button>
                        </FormGroup>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <StatsCard
                label='Total Checks'
                loading={rule_stats_loading}
                aggr_data={SfCommaStringFormatter(rule_stats?.[0]?.total_checks)}

              />
            </Col>
            <Col md={4}>
              <StatsCard
                label={RULE_STATUS.PASSED}
                aggr_data={SfCommaStringFormatter(rule_stats?.[0]?.num_passed)}
                percent_change={percentage(rule_stats?.[0]?.num_passed, rule_stats?.[0]?.total_checks)}
                loading={rule_stats_loading}
              />
            </Col>
            <Col md={4}>
              <StatsCard
                label={RULE_STATUS.FAILED}
                aggr_data={SfCommaStringFormatter(rule_stats?.[0]?.num_failed)}
                percent_change={percentage(rule_stats?.[0]?.num_failed, rule_stats?.[0]?.total_checks)}
                loading={rule_stats_loading}
              />
            </Col>
          </Row>
          <RulesDashboardTable
            selectedRuleName={selectedRuleName}
            selectedProductName={selectedProductName}
            selectedAccounts={selectedAccounts}
            fetchRuleLogsPage={fetchRuleLogsPage}
            onRuleLogsSortChange={onRuleLogsSortChange}
            ruleLogsSort={ruleLogsSort}
          />
        </Container>}
    </div >
  )
}

export default RulesDashboard
